/**
 * Storefront composition — slots (incl. header zones) + module keys + home opt-in.
 */

import { brand } from './brand';
import {
  DEFAULT_STOREFRONT_APPEARANCE,
  normalizeStorefrontAppearance,
} from './storefrontAppearance';
import {
  DEFAULT_STOREFRONT_CATALOG_SITE,
  normalizeStorefrontCatalogSite,
  type StorefrontCatalogSiteSettings,
} from './storefrontCatalogSite';
import {
  DEFAULT_STOREFRONT_SITE_CHROME,
  normalizeStorefrontSiteChrome,
} from './storefrontSiteChrome';
import {
  DEFAULT_STOREFRONT_SITE_HERO,
  normalizeStorefrontSiteHero,
} from './storefrontSiteHero';
import {
  DEFAULT_STOREFRONT_SITE_MESSAGE,
  normalizeStorefrontSiteMessage,
} from './storefrontSiteMessage';
import {
  DEFAULT_STOREFRONT_SITE_DICTIONARIES,
  normalizeStorefrontSiteDictionaries,
  type StorefrontSiteDictionariesSettings,
} from './storefrontSiteDictionaries';
import {
  contentTagDomId,
  contentTagForSiteBlock,
  normalizeContentTagRef,
} from './contentTags.js';
import { normalizeContactChrome } from './tenantContactLinks.js';
import { normalizeShopSlides, normalizeShopStartBanner } from './shopSlides.js';
import type { ContactChrome } from './tenantContactLinks.js';

export type StorefrontContactChrome = ContactChrome;

/** Full page regions + header chrome zones (constructor pieces). */
export const STOREFRONT_SLOTS = [
  'headerBrand',
  'headerNav',
  'headerLang',
  'headerAuth',
  'hero',
  'main1',
  'main2',
  'main3',
  'main4',
  'main5',
  'main6',
  'footer',
] as const;

export type StorefrontSlotId = (typeof STOREFRONT_SLOTS)[number];

export const STOREFRONT_HEADER_SLOTS = [
  'headerBrand',
  'headerNav',
  'headerLang',
  'headerAuth',
] as const;

/** Ordered main content blocks between hero and footer. */
export const STOREFRONT_MAIN_SLOTS = [
  'main1',
  'main2',
  'main3',
  'main4',
  'main5',
  'main6',
] as const;

export type StorefrontMainSlotId = (typeof STOREFRONT_MAIN_SLOTS)[number];

export const STOREFRONT_BODY_SLOTS = [
  'hero',
  'main1',
  'main2',
  'main3',
  'main4',
  'main5',
  'main6',
  'footer',
] as const;

/** Modules available in the constructor (chrome + content). */
export const STOREFRONT_MODULES = [
  'none',
  'brand',
  'brandLogo',
  'nav',
  'catalogMenu',
  'lang',
  'auth',
  'catalog',
  'contacts',
  'about',
  'bio',
  'message',
  'noteTag',
  'album',
  'portfolio',
  'favorites',
  'bodyHost',
] as const;

/** @deprecated migrated to noteTag + slotContentTags */
const LEGACY_BLOCK_MODULES = [
  'block1',
  'block2',
  'block3',
  'block4',
  'block5',
] as const;

export type StorefrontModuleId = (typeof STOREFRONT_MODULES)[number];

const MAIN_SLOT_MODULES: readonly StorefrontModuleId[] = [
  'none',
  'catalog',
  'about',
  'bio',
  'message',
  'contacts',
  'favorites',
  'bodyHost',
];

/** Includes noteTag for persistence/normalize; UI lists tags separately. */
const MAIN_SLOT_ALLOWED_MODULES: readonly StorefrontModuleId[] = [
  ...MAIN_SLOT_MODULES,
  'noteTag',
  'album',
  'portfolio',
];

export type StorefrontSlotContentTags = Partial<Record<StorefrontSlotId, string>>;

/** Gallery id bound to a slot when the module is `album`. */
export type StorefrontSlotAlbumIds = Partial<Record<StorefrontSlotId, string>>;

const SLOT_SET = new Set<string>(STOREFRONT_SLOTS);

export type StorefrontBindings = Record<StorefrontSlotId, StorefrontModuleId>;

export const STOREFRONT_MENU_ITEM_TYPES = [
  'catalogPopup',
  'scrollAbout',
  'scrollBio',
  'scrollCatalog',
  'scrollContacts',
  'scrollMessage',
  'scrollNoteTag',
  'scrollAlbum',
  'scrollPortfolio',
  'viewCatalog',
  'viewAbout',
  'viewBio',
  'viewRecommendations',
  'category',
] as const;

export type StorefrontMenuItemType = (typeof STOREFRONT_MENU_ITEM_TYPES)[number];

export type StorefrontBodyViewId = 'catalog' | 'about' | 'bio' | 'recommendations' | 'none';

export type StorefrontMenuItem =
  | { id: string; type: 'catalogPopup' }
  | { id: string; type: 'scrollAbout' }
  | { id: string; type: 'scrollBio' }
  | { id: string; type: 'scrollCatalog' }
  | { id: string; type: 'scrollContacts' }
  | { id: string; type: 'scrollMessage' }
  | { id: string; type: 'scrollNoteTag'; contentTag: string }
  | { id: string; type: 'scrollAlbum'; galleryId: string }
  | { id: string; type: 'scrollPortfolio' }
  | { id: string; type: 'viewCatalog' }
  | { id: string; type: 'viewAbout' }
  | { id: string; type: 'viewBio' }
  | { id: string; type: 'viewRecommendations' }
  | { id: string; type: 'category'; categoryId: string };

export type StorefrontContentWidth = 'full' | 'narrow';

/** How header menu renders: auto overflow → hamburger, or always hamburger. */
export const STOREFRONT_MENU_LAYOUTS = ['auto', 'hamburger'] as const;
export type StorefrontMenuLayout = (typeof STOREFRONT_MENU_LAYOUTS)[number];

export type StorefrontModuleSettings = {
  catalogMenu: {
    items: StorefrontMenuItem[];
    /** Menu item id selected as home (radio in constructor). */
    defaultItemId: string | null;
    /** Header menu chrome: auto | always hamburger. */
    menuLayout: StorefrontMenuLayout;
    /**
     * When home item is a scroll* anchor, scroll to it on page load.
     * Default off — stay on Hero / top until the user clicks the menu.
     */
    scrollHomeOnLoad: boolean;
  };
  /**
   * @deprecated Prefer `appearance.contentWidth`. Kept in sync for older readers.
   */
  contentWidth: StorefrontContentWidth;
  appearance: import('./storefrontAppearance').StorefrontAppearance;
  /** Website catalog tiles / filters chrome (Settings → Каталог сайта). */
  catalogSite: StorefrontCatalogSiteSettings;
  /** Menu separators, search overlay, FAB chrome (Settings → Прибамбасы). */
  siteChrome: import('./storefrontSiteChrome').StorefrontSiteChromeSettings;
  /** Splash gate + Hero block (Settings → Hero). */
  siteHero: import('./storefrontSiteHero').StorefrontSiteHeroSettings;
  /** Message form zone chrome (Settings → Message). */
  siteMessage: import('./storefrontSiteMessage').StorefrontSiteMessageSettings;
  /** Option-set dictionaries on the website (Settings → Справочники на сайте). */
  siteDictionaries: StorefrontSiteDictionariesSettings;
  /** Content tag for slots bound to `noteTag` (one note per tag). */
  slotContentTags: StorefrontSlotContentTags;
  /** Gallery id for slots bound to `album`. Dropped when the album leaves the site. */
  slotAlbumIds: StorefrontSlotAlbumIds;
  /**
   * Alive Album page order. Longer than the six main slots.
   * The first six rows are mirrored into main1–main6 for older readers.
   */
  pageRows: AliveAlbumStoredPageRow[];
  /**
   * The photographer saved the page list, including an empty one.
   * Until then, an untouched page shows both album grids.
   */
  albumGridsChosen: boolean;
  /**
   * Footer / floating contact chrome (Settings → Storefront).
   * Personal phone/social values stay on landing; this travels in UI presets.
   */
  contactChrome: StorefrontContactChrome;
  /** Home slider of a custom shop template (Mizranit). */
  shopSlides: import('./shopSlides').ShopSlide[];
  /** Full-screen greeting picture of a custom shop home (Mizranit). */
  shopStartBanner: import('./shopSlides').ShopStartBanner;
};

export type TenantStorefrontSettings = {
  /** When true, tenant `/` renders website shell from bindings (not classic landing). */
  homeEnabled: boolean;
  /**
   * When true, card appears in the platform landing manufacturer directory.
   * While off, storefront is only reachable by direct link / subdomain.
   */
  listOnPlatform: boolean;
  /**
   * When true, search engines may index the tenant storefront (robots + sitemap).
   * Default off until the owner opts in.
   */
  allowSearchIndexing: boolean;
  /** First hard-coded template id; more templates later. */
  templateId: string;
  bindings: StorefrontBindings;
  moduleSettings: StorefrontModuleSettings;
};

export const DEFAULT_STOREFRONT_BINDINGS: StorefrontBindings = {
  headerBrand: 'brand',
  headerNav: 'catalogMenu',
  headerLang: 'lang',
  headerAuth: 'auth',
  /** Always brand banner; chrome lives on Settings → Hero (not a module picker). */
  hero: 'brand',
  main1: 'bodyHost',
  main2: 'none',
  main3: 'none',
  main4: 'none',
  main5: 'none',
  main6: 'none',
  footer: 'contacts',
};

/** Which modules make sense in each slot (constructor selects). */
export const STOREFRONT_MODULES_FOR_SLOT: Record<StorefrontSlotId, readonly StorefrontModuleId[]> = {
  headerBrand: ['none', 'brand'],
  /** Site menu is on/off only — `nav` legacy maps to catalogMenu in normalize. */
  headerNav: ['none', 'catalogMenu'],
  headerLang: ['none', 'lang'],
  headerAuth: ['none', 'auth'],
  /** Hero is fixed brand banner — configure photo/logo/title on Settings → Hero. */
  hero: ['brand'],
  main1: MAIN_SLOT_ALLOWED_MODULES,
  main2: MAIN_SLOT_ALLOWED_MODULES,
  main3: MAIN_SLOT_ALLOWED_MODULES,
  main4: MAIN_SLOT_ALLOWED_MODULES,
  main5: MAIN_SLOT_ALLOWED_MODULES,
  main6: MAIN_SLOT_ALLOWED_MODULES,
  footer: ['none', 'contacts', 'about', 'bio', 'noteTag', 'message', 'nav'],
};

/** Tag bound to a slot when module is `noteTag`. */
export function resolveSlotContentTag(
  slot: StorefrontSlotId,
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
): string {
  if (bindings[slot] !== 'noteTag') return '';
  return normalizeContentTagRef(slotContentTags[slot]);
}

/** Encode slot picker value (static module, noteTag:my:…, or album:<id>). */
export function encodeSlotModulePickerValue(
  slot: StorefrontSlotId,
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
  slotAlbumIds: StorefrontSlotAlbumIds = {},
): string {
  const mod = bindings[slot];
  if (mod === 'noteTag') {
    const tag = resolveSlotContentTag(slot, bindings, slotContentTags);
    return tag ? `noteTag:${tag}` : 'noteTag';
  }
  if (mod === 'album') {
    const id = String(slotAlbumIds[slot] || '').trim();
    return id ? `album:${id}` : 'album';
  }
  return mod;
}

/** Parse slot picker value into module + optional content tag. */
export function parseSlotModulePickerValue(raw: string): {
  moduleId: StorefrontModuleId;
  contentTag?: string;
} {
  const value = String(raw || '').trim();
  if (value.startsWith('noteTag:')) {
    return {
      moduleId: 'noteTag',
      contentTag: normalizeContentTagRef(value.slice('noteTag:'.length)),
    };
  }
  if (value === 'noteTag') {
    return { moduleId: 'noteTag' };
  }
  if (value.startsWith('album:')) {
    return {
      moduleId: 'album',
      contentTag: value.slice('album:'.length).trim().toLowerCase(),
    };
  }
  if (value === 'album') {
    return { moduleId: 'album' };
  }
  return { moduleId: normalizeStorefrontModule(value) };
}

export function migrateLegacyBlockBindings(
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
): { bindings: StorefrontBindings; slotContentTags: StorefrontSlotContentTags } {
  const nextBindings = { ...bindings };
  const nextTags = { ...slotContentTags };
  for (const slot of STOREFRONT_SLOTS) {
    const mod = nextBindings[slot];
    if (!(LEGACY_BLOCK_MODULES as readonly string[]).includes(mod)) continue;
    nextBindings[slot] = 'noteTag';
    if (!nextTags[slot]) {
      nextTags[slot] = contentTagForSiteBlock(mod);
    }
  }
  return { bindings: nextBindings, slotContentTags: nextTags };
}

/** Default module when turning a main slot on via checkbox. */
export function defaultModuleForMainSlot(slot: StorefrontMainSlotId): StorefrontModuleId {
  return slot === 'main1' ? 'bodyHost' : 'catalog';
}

/** DOM id for a main slot region on the live website. */
export function storefrontMainSlotDomId(slot: StorefrontMainSlotId): string {
  return `website-slot-main-${slot.replace('main', '')}`;
}

/** Enabled main slots in page order (binding !== none). */
export function listEnabledMainSlots(
  bindings: StorefrontBindings,
): StorefrontMainSlotId[] {
  return STOREFRONT_MAIN_SLOTS.filter((slot) => bindings[slot] !== 'none');
}

export function isStorefrontMainSlotId(value: unknown): value is StorefrontMainSlotId {
  return (
    value === 'main1'
    || value === 'main2'
    || value === 'main3'
    || value === 'main4'
    || value === 'main5'
    || value === 'main6'
  );
}

/**
 * Keep at most one bodyHost among main slots.
 * Other mains that were bodyHost fall back to catalog.
 */
export function applyExclusiveBodyHost(
  bindings: StorefrontBindings,
  slot: StorefrontMainSlotId,
  moduleId: StorefrontModuleId,
): StorefrontBindings {
  const next = { ...bindings, [slot]: moduleId };
  if (moduleId !== 'bodyHost') return next;
  for (const other of STOREFRONT_MAIN_SLOTS) {
    if (other === slot) continue;
    if (next[other] === 'bodyHost') next[other] = 'catalog';
  }
  return next;
}

export function newStorefrontMenuItemId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `mi_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable ids so autosync does not churn dirty checks. */
export const AUTO_MENU_ITEM_IDS = {
  catalogPopup: 'auto-catalog-popup',
  scrollAbout: 'auto-scroll-about',
  scrollBio: 'auto-scroll-bio',
  scrollCatalog: 'auto-scroll-catalog',
  scrollContacts: 'auto-scroll-contacts',
  scrollMessage: 'auto-scroll-message',
  viewCatalog: 'auto-view-catalog',
  viewAbout: 'auto-view-about',
  viewBio: 'auto-view-bio',
  viewRecommendations: 'auto-view-recommendations',
} as const;

/** Stable defaults when DB has no items yet (ids fixed so settings dirty-check stays calm). */
export const DEFAULT_CATALOG_MENU_ITEMS: StorefrontMenuItem[] = [
  { id: AUTO_MENU_ITEM_IDS.viewCatalog, type: 'viewCatalog' },
  { id: AUTO_MENU_ITEM_IDS.scrollAbout, type: 'scrollAbout' },
  { id: AUTO_MENU_ITEM_IDS.scrollContacts, type: 'scrollContacts' },
];

export function defaultCatalogMenuItems(): StorefrontMenuItem[] {
  return DEFAULT_CATALOG_MENU_ITEMS.map((item) => ({ ...item }));
}

export const DEFAULT_STOREFRONT_MODULE_SETTINGS: StorefrontModuleSettings = {
  catalogMenu: {
    items: defaultCatalogMenuItems(),
    defaultItemId: null,
    menuLayout: 'auto',
    scrollHomeOnLoad: false,
  },
  contentWidth: 'narrow',
  appearance: { ...DEFAULT_STOREFRONT_APPEARANCE },
  catalogSite: { ...DEFAULT_STOREFRONT_CATALOG_SITE },
  siteChrome: { ...DEFAULT_STOREFRONT_SITE_CHROME },
  siteHero: { ...DEFAULT_STOREFRONT_SITE_HERO },
  siteMessage: { ...DEFAULT_STOREFRONT_SITE_MESSAGE },
  siteDictionaries: { ...DEFAULT_STOREFRONT_SITE_DICTIONARIES },
  slotContentTags: {},
  slotAlbumIds: {},
  pageRows: [],
  contactChrome: normalizeContactChrome(null),
  shopSlides: [],
  shopStartBanner: normalizeShopStartBanner(null),
};

/** Shop home that follows the Made in Holyland vitrine. Other tenants keep the lighting shell. */
export const HOLY_LAND_SHOP_TEMPLATE = 'holy_land_shop_v1';

/** Mattress shop home: slider, category blocks with text, product grid. */
export const MIZRANIT_SHOP_TEMPLATE = 'mizranit_v1';

/** Only templates that print category text show the description field in the cabinet. */
export function storefrontShowsCategoryText(storefront: unknown): boolean {
  return normalizeStorefrontSettings(storefront).templateId === MIZRANIT_SHOP_TEMPLATE;
}

export const DEFAULT_STOREFRONT_SETTINGS: TenantStorefrontSettings = {
  homeEnabled: false,
  listOnPlatform: false,
  allowSearchIndexing: false,
  templateId: 'brand_lighting_v1',
  bindings: { ...DEFAULT_STOREFRONT_BINDINGS },
  moduleSettings: {
    ...DEFAULT_STOREFRONT_MODULE_SETTINGS,
    catalogMenu: {
      items: defaultCatalogMenuItems(),
      defaultItemId: null,
      menuLayout: 'auto',
      scrollHomeOnLoad: false,
    },
    appearance: { ...DEFAULT_STOREFRONT_APPEARANCE },
    catalogSite: { ...DEFAULT_STOREFRONT_CATALOG_SITE },
    siteChrome: { ...DEFAULT_STOREFRONT_SITE_CHROME },
    siteHero: { ...DEFAULT_STOREFRONT_SITE_HERO },
    siteMessage: { ...DEFAULT_STOREFRONT_SITE_MESSAGE },
    siteDictionaries: { ...DEFAULT_STOREFRONT_SITE_DICTIONARIES },
    contactChrome: normalizeContactChrome(null),
  },
};

export function isStorefrontSlotId(value: unknown): value is StorefrontSlotId {
  return typeof value === 'string' && SLOT_SET.has(value);
}

export function normalizeStorefrontModule(raw: unknown): StorefrontModuleId {
  const id = String(raw || '').trim().toLowerCase();
  // Legacy single blog module → note tag my:block1
  if (id === 'blog') return 'noteTag';
  if ((LEGACY_BLOCK_MODULES as readonly string[]).includes(id)) return 'noteTag';
  for (const mod of STOREFRONT_MODULES) {
    if (mod.toLowerCase() === id) return mod;
  }
  return 'none';
}

/**
 * Hero is always the brand banner. Legacy content modules in `hero` move to the
 * first free main slot; `brandLogo` / `none` become `brand`.
 */
export function migrateHeroSlotToBrandBanner(bindings: StorefrontBindings): StorefrontBindings {
  const next: StorefrontBindings = { ...bindings };
  const heroMod = next.hero;
  if (heroMod === 'brand') return next;
  if (heroMod === 'brandLogo' || heroMod === 'none') {
    next.hero = 'brand';
    return next;
  }
  let placed = false;
  for (const slot of STOREFRONT_MAIN_SLOTS) {
    if (next[slot] === 'none') {
      next[slot] = heroMod;
      placed = true;
      break;
    }
  }
  if (!placed) {
    for (let i = STOREFRONT_MAIN_SLOTS.length - 1; i >= 0; i -= 1) {
      const slot = STOREFRONT_MAIN_SLOTS[i];
      if (next[slot] !== 'bodyHost') {
        next[slot] = heroMod;
        placed = true;
        break;
      }
    }
  }
  next.hero = 'brand';
  return next;
}

export function normalizeStorefrontBindings(raw: unknown): StorefrontBindings {
  const source =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const bindings = { ...DEFAULT_STOREFRONT_BINDINGS };
  for (const slot of STOREFRONT_SLOTS) {
    let rawVal = source[slot];
    // Legacy single `main` slot → main1.
    if (slot === 'main1' && rawVal == null && source.main != null) {
      rawVal = source.main;
    }
    if (rawVal == null) continue;
    let mod = normalizeStorefrontModule(rawVal);
    // Legacy header "simple anchors" → site menu on.
    if (slot === 'headerNav' && mod === 'nav') mod = 'catalogMenu';
    if (slot === 'hero') {
      // Accept legacy modules here; migrateHeroSlotToBrandBanner relocates them.
      bindings.hero = mod;
      continue;
    }
    const allowed = STOREFRONT_MODULES_FOR_SLOT[slot];
    bindings[slot] = allowed.includes(mod) ? mod : DEFAULT_STOREFRONT_BINDINGS[slot];
  }
  // At most one bodyHost among mains after normalize.
  let seenHost = false;
  for (const slot of STOREFRONT_MAIN_SLOTS) {
    if (bindings[slot] !== 'bodyHost') continue;
    if (!seenHost) {
      seenHost = true;
      continue;
    }
    bindings[slot] = 'catalog';
  }
  return migrateHeroSlotToBrandBanner(bindings);
}

export function bodySlotsIncludeModule(
  bindings: StorefrontBindings,
  moduleId: StorefrontModuleId,
): boolean {
  if (bindings.hero === moduleId || bindings.footer === moduleId) return true;
  return STOREFRONT_MAIN_SLOTS.some((slot) => bindings[slot] === moduleId);
}

/**
 * Modules that can produce scroll anchors in the menu.
 * bodyHost slots are scenes — they do not contribute scroll anchors.
 */
export function scrollSlotsIncludeModule(
  bindings: StorefrontBindings,
  moduleId: StorefrontModuleId,
): boolean {
  if (bindings.hero === moduleId || bindings.footer === moduleId) return true;
  for (const slot of STOREFRONT_MAIN_SLOTS) {
    const mod = bindings[slot];
    if (mod === moduleId && mod !== 'bodyHost') return true;
  }
  return false;
}

export function storefrontUsesBodyHost(bindings: StorefrontBindings): boolean {
  return STOREFRONT_MAIN_SLOTS.some((slot) => bindings[slot] === 'bodyHost');
}

/** Contacts footer / StorefrontFooter is shown for contacts or none. */
export function storefrontShowsContactsAnchor(bindings: StorefrontBindings): boolean {
  if (bindings.footer === 'contacts' || bindings.footer === 'none') return true;
  if (bindings.hero === 'contacts') return true;
  for (const slot of STOREFRONT_MAIN_SLOTS) {
    if (bindings[slot] === 'contacts') return true;
  }
  return false;
}

export function storefrontShowsAboutAnchor(bindings: StorefrontBindings): boolean {
  return scrollSlotsIncludeModule(bindings, 'about');
}

export function storefrontShowsBioAnchor(bindings: StorefrontBindings): boolean {
  return scrollSlotsIncludeModule(bindings, 'bio');
}

export function storefrontShowsCatalogAnchor(bindings: StorefrontBindings): boolean {
  return scrollSlotsIncludeModule(bindings, 'catalog');
}

export function storefrontShowsMessageAnchor(bindings: StorefrontBindings): boolean {
  return scrollSlotsIncludeModule(bindings, 'message');
}

export function storefrontShowsNoteTagAnchor(
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
  contentTag: string,
): boolean {
  const tag = normalizeContentTagRef(contentTag);
  if (!tag) return false;
  for (const slot of STOREFRONT_BODY_SLOTS) {
    if (bindings[slot] !== 'noteTag') continue;
    if (resolveSlotContentTag(slot, bindings, slotContentTags) === tag) return true;
  }
  return false;
}

/** @deprecated use storefrontShowsNoteTagAnchor */
export function storefrontShowsBlockAnchor(
  bindings: StorefrontBindings,
  block: 'block1' | 'block2' | 'block3' | 'block4' | 'block5',
): boolean {
  return scrollSlotsIncludeModule(bindings, 'noteTag');
}

/** @deprecated use storefrontShowsBlockAnchor */
export function storefrontShowsBlogAnchor(bindings: StorefrontBindings): boolean {
  return storefrontShowsBlockAnchor(bindings, 'block1');
}

export type StorefrontSceneViewType = 'viewCatalog' | 'viewAbout' | 'viewBio';

const SCENE_VIEW_TYPES: readonly StorefrontSceneViewType[] = [
  'viewCatalog',
  'viewAbout',
  'viewBio',
];

const BODY_SLOT_ORDER = [
  'hero',
  'main1',
  'main2',
  'main3',
  'main4',
  'main5',
  'main6',
  'footer',
] as const;

function findPrevItem(
  previous: readonly StorefrontMenuItem[],
  type: StorefrontMenuItemType,
): StorefrontMenuItem | undefined {
  return previous.find((item) => item.type === type);
}

function singletonItem(
  previous: readonly StorefrontMenuItem[],
  type: Exclude<StorefrontMenuItemType, 'category'>,
  stableId: string,
): StorefrontMenuItem {
  const prev = findPrevItem(previous, type);
  if (prev && prev.type === type) return prev;
  return { id: stableId, type } as StorefrontMenuItem;
}

function scrollNoteTagItem(
  previous: readonly StorefrontMenuItem[],
  contentTag: string,
): StorefrontMenuItem {
  const tag = normalizeContentTagRef(contentTag);
  const prev = previous.find(
    (item) => item.type === 'scrollNoteTag' && item.contentTag === tag,
  );
  if (prev) return prev;
  return { id: newStorefrontMenuItemId(), type: 'scrollNoteTag', contentTag: tag };
}

function scrollAlbumItem(
  previous: readonly StorefrontMenuItem[],
  galleryId: string,
): StorefrontMenuItem {
  const id = galleryId.trim().toLowerCase();
  const prev = previous.find(
    (item) => item.type === 'scrollAlbum' && item.galleryId === id,
  );
  if (prev) return prev;
  return { id: `album-${id}`, type: 'scrollAlbum', galleryId: id };
}

function menuItemRequirementKey(item: StorefrontMenuItem): string {
  if (item.type === 'scrollNoteTag') return `scrollNoteTag:${item.contentTag}`;
  if (item.type === 'scrollAlbum') return `scrollAlbum:${item.galleryId}`;
  if (item.type === 'category') return `category:${item.categoryId}`;
  return item.type;
}

function scrollItemForModule(
  moduleId: StorefrontModuleId,
  previous: readonly StorefrontMenuItem[],
): StorefrontMenuItem | null {
  if (moduleId === 'about') {
    return singletonItem(previous, 'scrollAbout', AUTO_MENU_ITEM_IDS.scrollAbout);
  }
  if (moduleId === 'bio') {
    return singletonItem(previous, 'scrollBio', AUTO_MENU_ITEM_IDS.scrollBio);
  }
  if (moduleId === 'catalog') {
    return singletonItem(previous, 'scrollCatalog', AUTO_MENU_ITEM_IDS.scrollCatalog);
  }
  if (moduleId === 'contacts') {
    return singletonItem(previous, 'scrollContacts', AUTO_MENU_ITEM_IDS.scrollContacts);
  }
  if (moduleId === 'message') {
    return singletonItem(previous, 'scrollMessage', AUTO_MENU_ITEM_IDS.scrollMessage);
  }
  return null;
}

/**
 * Build menu from body slots (hero → main1–6 → footer).
 * Ordinary modules → scroll anchors. bodyHost → scene screens (at least Catalog).
 * Preserves prior order when items remain valid.
 */
export function syncCatalogMenuItemsWithBindings(
  bindings: StorefrontBindings,
  previous: readonly StorefrontMenuItem[],
  slotContentTags: StorefrontSlotContentTags = {},
  slotAlbumIds: StorefrontSlotAlbumIds = {},
): StorefrontMenuItem[] {
  const prevList = previous.length > 0 ? previous : defaultCatalogMenuItems();
  const required: StorefrontMenuItem[] = [];
  const requiredKeys = new Set<string>();

  const pushRequired = (item: StorefrontMenuItem) => {
    const key = menuItemRequirementKey(item);
    if (requiredKeys.has(key)) return;
    requiredKeys.add(key);
    required.push(item);
  };

  for (const slot of BODY_SLOT_ORDER) {
    const mod = bindings[slot];
    if (mod === 'none' || mod === 'brand' || mod === 'brandLogo' || mod === 'nav' || mod === 'favorites') continue;

    if (mod === 'bodyHost') {
      let views = SCENE_VIEW_TYPES.filter((type) => prevList.some((item) => item.type === type));
      // Do not duplicate content already present as a scroll anchor elsewhere.
      if (storefrontShowsAboutAnchor(bindings)) {
        views = views.filter((type) => type !== 'viewAbout');
      }
      if (storefrontShowsBioAnchor(bindings)) {
        views = views.filter((type) => type !== 'viewBio');
      }
      if (storefrontShowsCatalogAnchor(bindings)) {
        views = views.filter((type) => type !== 'viewCatalog');
      }
      // Need a scene radio in the menu only when Catalog is not already a scroll anchor.
      // Forcing viewCatalog while scrollCatalog exists re-injected a duplicate and shoved it last on save.
      if (views.length === 0 && !storefrontShowsCatalogAnchor(bindings)) {
        views = ['viewCatalog'];
      }
      for (const type of views) {
        pushRequired(singletonItem(prevList, type, AUTO_MENU_ITEM_IDS[type]));
      }
      continue;
    }

    if (mod === 'noteTag') {
      const tag = resolveSlotContentTag(slot, bindings, slotContentTags);
      if (tag) pushRequired(scrollNoteTagItem(prevList, tag));
      continue;
    }

    if (mod === 'album') {
      const galleryId = String(slotAlbumIds[slot] || '').trim();
      if (galleryId) pushRequired(scrollAlbumItem(prevList, galleryId));
      continue;
    }

    const scroll = scrollItemForModule(mod, prevList);
    if (scroll) pushRequired(scroll);
  }

  // Footer contacts chrome still counts when footer is none.
  if (
    !requiredKeys.has('scrollContacts')
    && (bindings.footer === 'none' || bindings.footer === 'contacts')
  ) {
    pushRequired(singletonItem(prevList, 'scrollContacts', AUTO_MENU_ITEM_IDS.scrollContacts));
  }

  // Preserve user order for still-valid items; append any missing required.
  // Keep manually added category shortcuts (not driven by slots).
  const next: StorefrontMenuItem[] = [];
  const seen = new Set<string>();
  const seenCategoryIds = new Set<string>();
  for (const item of prevList) {
    if (item.type === 'catalogPopup') continue;
    if (item.type === 'category') {
      if (!item.categoryId || seenCategoryIds.has(item.categoryId)) continue;
      seenCategoryIds.add(item.categoryId);
      next.push(item);
      continue;
    }
    // Recommendations is a manually attached bodyHost scene, not a slot-derived module.
    if (item.type === 'viewRecommendations') {
      const key = menuItemRequirementKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      next.push(item);
      continue;
    }
    const key = menuItemRequirementKey(item);
    if (!requiredKeys.has(key)) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(item);
  }
  // Missing required items: insert by relative slot-derived order, not always at the end.
  for (const item of required) {
    const key = menuItemRequirementKey(item);
    if (seen.has(key)) continue;
    const reqIdx = required.findIndex((r) => menuItemRequirementKey(r) === key);
    let insertAt = next.length;
    for (let i = 0; i < next.length; i += 1) {
      const n = next[i];
      if (n.type === 'category') continue;
      const nIdx = required.findIndex((r) => menuItemRequirementKey(r) === menuItemRequirementKey(n));
      if (nIdx === -1) continue;
      if (nIdx > reqIdx) {
        insertAt = i;
        break;
      }
    }
    next.splice(insertAt, 0, item);
    seen.add(key);
  }

  return next.length > 0 ? next : defaultCatalogMenuItems();
}

/** Shop scenes that do not belong on a photographer page. */
const ALIVE_ALBUM_SHOP_MAIN_MODULES = new Set<StorefrontModuleId>([
  'bodyHost',
  'catalog',
  'favorites',
]);

const ALIVE_ALBUM_PAGE_MODULES = [
  'about',
  'bio',
  'message',
  'contacts',
  'noteTag',
  'album',
  'portfolio',
  'ownAlbums',
  'sharedAlbums',
] as const;

export type AliveAlbumPageModule = (typeof ALIVE_ALBUM_PAGE_MODULES)[number];

/** How an album row paints on the page. Missing means one cover. */
export const ALIVE_ALBUM_VIEWS = ['cover', 'slider', 'fade', 'grid'] as const;
export type AliveAlbumAlbumView = (typeof ALIVE_ALBUM_VIEWS)[number];

/** Rows of a grid view. Columns follow the screen width. */
export const ALIVE_ALBUM_GRIDS = ['1', '2', '3', '4'] as const;
export type AliveAlbumAlbumGrid = (typeof ALIVE_ALBUM_GRIDS)[number];

/** Rows of the old fixed column × row grids. */
const LEGACY_ALBUM_GRIDS: Record<string, AliveAlbumAlbumGrid> = { '2x2': '2', '3x2': '2', '4x3': '3' };

/** Width:height of each frame. `asis` keeps the photo's own shape. */
export const ALIVE_ALBUM_RATIOS = ['asis', '1:1', '2:3', '4:5'] as const;
export type AliveAlbumAlbumRatio = (typeof ALIVE_ALBUM_RATIOS)[number];

/** Height of the album area as a share of the window height. `auto` follows the photo at the full column width. */
export const ALIVE_ALBUM_HEIGHTS = ['auto', '40', '60', '80', '100'] as const;
export type AliveAlbumAlbumHeight = (typeof ALIVE_ALBUM_HEIGHTS)[number];

/** Caption of an album grid. An empty string hides the heading in that language. */
export type AliveAlbumGridTitle = { ru: string; en: string; he: string };

/** Shown until the photographer writes their own, or clears the field. */
export const ALIVE_ALBUM_GRID_TITLE_DEFAULTS: Record<'ownAlbums' | 'sharedAlbums', AliveAlbumGridTitle> = {
  ownAlbums: { ru: 'Мои альбомы', en: 'My albums', he: 'האלבומים שלי' },
  sharedAlbums: { ru: 'Поделились', en: 'Shared', he: 'שותפו' },
};

/** Saved row. `ref` is a note tag or a gallery id. Album view fields stay on the row. */
export type AliveAlbumStoredPageRow = {
  moduleId: AliveAlbumPageModule;
  ref: string;
  albumView?: AliveAlbumAlbumView;
  albumGrid?: AliveAlbumAlbumGrid;
  albumRatio?: AliveAlbumAlbumRatio;
  albumHeight?: AliveAlbumAlbumHeight;
  /** False keeps the block on the page without opening the album. Missing means open. */
  albumOpen?: boolean;
  /** Own and shared grids. Missing means the default words. An empty string hides that language. */
  gridTitle?: AliveAlbumGridTitle;
};

/** Builder row. `sourceSlot` lets appearance follow the row while it stays inside main1–main6. */
export type AliveAlbumPageRow = AliveAlbumStoredPageRow & {
  sourceSlot: StorefrontMainSlotId | null;
};

const ALIVE_ALBUM_PAGE_MODULE_SET = new Set<string>(ALIVE_ALBUM_PAGE_MODULES);

function readGridTitle(
  raw: unknown,
  moduleId: 'ownAlbums' | 'sharedAlbums',
): AliveAlbumGridTitle {
  const fallback = ALIVE_ALBUM_GRID_TITLE_DEFAULTS[moduleId];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...fallback };
  const source = raw as Record<string, unknown>;
  const pick = (key: 'ru' | 'en' | 'he') => (
    Object.prototype.hasOwnProperty.call(source, key) ? String(source[key] ?? '').trim() : fallback[key]
  );
  return { ru: pick('ru'), en: pick('en'), he: pick('he') };
}

/** Heading for a grid row. A missing title is the default; a stored empty string stays empty. */
export function aliveAlbumGridTitle(row: AliveAlbumStoredPageRow): AliveAlbumGridTitle {
  if (row.moduleId !== 'ownAlbums' && row.moduleId !== 'sharedAlbums') {
    return { ru: '', en: '', he: '' };
  }
  return readGridTitle(row.gridTitle, row.moduleId);
}

function albumGridRow(moduleId: 'ownAlbums' | 'sharedAlbums'): AliveAlbumStoredPageRow {
  return { moduleId, ref: '', gridTitle: { ...ALIVE_ALBUM_GRID_TITLE_DEFAULTS[moduleId] } };
}

/**
 * Untouched page shows both grids. A page that is only auto-added share rows
 * becomes those grids too. Once the photographer saves the list, it stays as saved,
 * including when they removed a grid.
 */
export function presentAliveAlbumPageRows<T extends AliveAlbumStoredPageRow>(
  rows: readonly T[],
  chosen: boolean,
): T[] {
  if (chosen) return rows.slice();
  const shareRow = (row: AliveAlbumStoredPageRow) => row.moduleId === 'album' && row.ref.startsWith('share:');
  const asGrid = (moduleId: 'ownAlbums' | 'sharedAlbums'): T => ({
    ...albumGridRow(moduleId),
    sourceSlot: null,
  }) as T;
  if (rows.length === 0 || rows.every(shareRow)) {
    return [asGrid('ownAlbums'), asGrid('sharedAlbums')];
  }
  if (rows.some(shareRow) && !rows.some((row) => row.moduleId === 'sharedAlbums')) {
    const next: T[] = [];
    let placed = false;
    for (const row of rows) {
      if (shareRow(row)) {
        if (!placed) {
          next.push(asGrid('sharedAlbums'));
          placed = true;
        }
        continue;
      }
      next.push(row);
    }
    return next;
  }
  return rows.slice();
}

function readAlbumRowView(record: {
  albumView?: unknown;
  albumGrid?: unknown;
  albumRatio?: unknown;
  albumHeight?: unknown;
  albumOpen?: unknown;
}): Pick<AliveAlbumStoredPageRow, 'albumView' | 'albumGrid' | 'albumRatio' | 'albumHeight' | 'albumOpen'> {
  const viewRaw = String(record.albumView || '');
  const gridRaw = String(record.albumGrid || '');
  const ratioRaw = String(record.albumRatio || '');
  const heightRaw = String(record.albumHeight || '');
  const albumView = (ALIVE_ALBUM_VIEWS as readonly string[]).includes(viewRaw)
    ? (viewRaw as AliveAlbumAlbumView)
    : 'cover';
  const albumGrid = (ALIVE_ALBUM_GRIDS as readonly string[]).includes(gridRaw)
    ? (gridRaw as AliveAlbumAlbumGrid)
    : (LEGACY_ALBUM_GRIDS[gridRaw] || '2');
  const albumRatio = (ALIVE_ALBUM_RATIOS as readonly string[]).includes(ratioRaw)
    ? (ratioRaw as AliveAlbumAlbumRatio)
    : 'asis';
  const albumHeight = (ALIVE_ALBUM_HEIGHTS as readonly string[]).includes(heightRaw)
    ? (heightRaw as AliveAlbumAlbumHeight)
    : 'auto';
  const albumOpen = record.albumOpen !== false && record.albumOpen !== 'false' && record.albumOpen !== 0;
  return { albumView, albumGrid, albumRatio, albumHeight, albumOpen };
}

export function normalizeAliveAlbumPageRows(raw: unknown): AliveAlbumStoredPageRow[] {
  if (!Array.isArray(raw)) return [];
  const out: AliveAlbumStoredPageRow[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const record = item as {
      moduleId?: unknown;
      ref?: unknown;
      albumView?: unknown;
      albumGrid?: unknown;
      albumRatio?: unknown;
      albumHeight?: unknown;
      albumOpen?: unknown;
      gridTitle?: unknown;
    };
    const moduleId = String(record.moduleId || '');
    if (!ALIVE_ALBUM_PAGE_MODULE_SET.has(moduleId)) continue;
    let ref = '';
    if (moduleId === 'noteTag') {
      ref = normalizeContentTagRef(record.ref);
      if (!ref) continue;
    } else if (moduleId === 'album') {
      ref = String(record.ref || '').trim().toLowerCase();
      if (!ref) continue;
    }
    const key = moduleId === 'noteTag' || moduleId === 'album' ? `${moduleId}:${ref}` : moduleId;
    if (seen.has(key)) continue;
    seen.add(key);
    const row: AliveAlbumStoredPageRow = { moduleId: moduleId as AliveAlbumPageModule, ref };
    if (moduleId === 'album' || moduleId === 'portfolio') Object.assign(row, readAlbumRowView(record));
    if (moduleId === 'ownAlbums' || moduleId === 'sharedAlbums') {
      row.gridTitle = readGridTitle(record.gridTitle, moduleId);
    }
    out.push(row);
  }
  return out;
}

function rowsFromMainSlots(
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
  slotAlbumIds: StorefrontSlotAlbumIds,
): AliveAlbumStoredPageRow[] {
  const rows: AliveAlbumStoredPageRow[] = [];
  for (const slot of STOREFRONT_MAIN_SLOTS) {
    const mod = bindings[slot];
    if (mod === 'about' || mod === 'bio' || mod === 'message' || mod === 'contacts') {
      rows.push({ moduleId: mod, ref: '' });
      continue;
    }
    if (mod === 'noteTag') {
      const ref = resolveSlotContentTag(slot, bindings, slotContentTags);
      if (ref) rows.push({ moduleId: 'noteTag', ref });
      continue;
    }
    if (mod === 'album') {
      const ref = String(slotAlbumIds[slot] || '').trim().toLowerCase();
      if (ref) rows.push({ moduleId: 'album', ref });
    }
  }
  return rows;
}

/** Copy the first six rows into main slots. Later rows live only in `pageRows`. */
export function mirrorAliveAlbumPageRows(
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
  slotAlbumIds: StorefrontSlotAlbumIds,
  rows: readonly AliveAlbumStoredPageRow[],
): {
  bindings: StorefrontBindings;
  slotContentTags: StorefrontSlotContentTags;
  slotAlbumIds: StorefrontSlotAlbumIds;
} {
  const nextBindings = stripAliveAlbumShopMainSlots({ ...bindings });
  const tags = { ...slotContentTags };
  const albums = { ...slotAlbumIds };
  for (const slot of STOREFRONT_MAIN_SLOTS) {
    nextBindings[slot] = 'none';
    delete tags[slot];
    delete albums[slot];
  }
  rows.forEach((row, index) => {
    const slot = STOREFRONT_MAIN_SLOTS[index];
    if (!slot) return;
    if (row.moduleId === 'ownAlbums' || row.moduleId === 'sharedAlbums') return;
    nextBindings[slot] = row.moduleId;
    if (row.moduleId === 'noteTag' && row.ref) tags[slot] = row.ref;
    if (row.moduleId === 'album' && row.ref) albums[slot] = row.ref;
  });
  return { bindings: nextBindings, slotContentTags: tags, slotAlbumIds: albums };
}

export function stripAliveAlbumShopMainSlots(
  bindings: StorefrontBindings,
): StorefrontBindings {
  const next = { ...bindings };
  for (const slot of STOREFRONT_MAIN_SLOTS) {
    if (ALIVE_ALBUM_SHOP_MAIN_MODULES.has(next[slot])) next[slot] = 'none';
  }
  return next;
}

export function readAliveAlbumPageRows(
  settings: TenantStorefrontSettings,
): AliveAlbumPageRow[] {
  const stored = settings.moduleSettings.pageRows ?? [];
  if (brand.id === 'alivealbum') {
    return stored.map((row, index) => ({
      ...row,
      sourceSlot: STOREFRONT_MAIN_SLOTS[index] ?? null,
    }));
  }
  return rowsFromMainSlots(
    settings.bindings,
    settings.moduleSettings.slotContentTags ?? {},
    settings.moduleSettings.slotAlbumIds ?? {},
  ).map((row, index) => ({
    ...row,
    sourceSlot: STOREFRONT_MAIN_SLOTS[index] ?? null,
  }));
}

/**
 * Menu follows the page rows, in that order.
 * Does not invent a catalog screen when the list is empty.
 * A note row is included here; the public menu hides it when the note has no title.
 */
export function syncAliveAlbumPageMenu(
  rows: readonly AliveAlbumStoredPageRow[],
  previous: readonly StorefrontMenuItem[],
): StorefrontMenuItem[] {
  const items: StorefrontMenuItem[] = [];
  for (const row of rows) {
    if (row.moduleId === 'noteTag') {
      if (row.ref) items.push(scrollNoteTagItem(previous, row.ref));
      continue;
    }
    if (row.moduleId === 'album') {
      if (row.ref) items.push(scrollAlbumItem(previous, row.ref));
      continue;
    }
    if (row.moduleId === 'portfolio') {
      items.push(singletonItem(previous, 'scrollPortfolio', 'portfolio'));
      continue;
    }
    if (row.moduleId === 'about' || row.moduleId === 'bio' || row.moduleId === 'message' || row.moduleId === 'contacts') {
      const scroll = scrollItemForModule(row.moduleId, previous);
      if (scroll) items.push(scroll);
    }
  }
  return items;
}

/** Write the page list. Rows past main6 stay in `pageRows` and still join the menu. */
export function applyAliveAlbumPageRows(
  settings: TenantStorefrontSettings,
  rows: readonly AliveAlbumPageRow[],
): TenantStorefrontSettings {
  const stored = normalizeAliveAlbumPageRows(rows);
  const mirrored = mirrorAliveAlbumPageRows(
    settings.bindings,
    settings.moduleSettings.slotContentTags ?? {},
    settings.moduleSettings.slotAlbumIds ?? {},
    stored,
  );
  const presentation = {
    ...(settings.moduleSettings.appearance.slotPresentation ?? {}),
  };
  for (const slot of STOREFRONT_MAIN_SLOTS) delete presentation[slot];
  stored.forEach((row, index) => {
    const slot = STOREFRONT_MAIN_SLOTS[index];
    const from = rows[index]?.sourceSlot;
    if (!slot || !from) return;
    const prev = settings.moduleSettings.appearance.slotPresentation?.[from];
    if (prev) presentation[slot] = prev;
  });
  const items = syncAliveAlbumPageMenu(stored, settings.moduleSettings.catalogMenu.items);
  return {
    ...settings,
    bindings: mirrored.bindings,
    moduleSettings: {
      ...settings.moduleSettings,
      slotContentTags: mirrored.slotContentTags,
      slotAlbumIds: mirrored.slotAlbumIds,
      pageRows: stored,
      albumGridsChosen: true,
      appearance: {
        ...settings.moduleSettings.appearance,
        slotPresentation: presentation,
      },
      catalogMenu: {
        ...settings.moduleSettings.catalogMenu,
        items,
        defaultItemId: patchCatalogMenuDefaultId(
          items,
          settings.moduleSettings.catalogMenu.defaultItemId,
        ),
      },
    },
  };
}

export function syncBoundStorefrontMenu(
  bindings: StorefrontBindings,
  previous: readonly StorefrontMenuItem[],
  slotContentTags: StorefrontSlotContentTags = {},
  slotAlbumIds: StorefrontSlotAlbumIds = {},
  pageRows: readonly AliveAlbumStoredPageRow[] = [],
): StorefrontMenuItem[] {
  if (brand.id === 'alivealbum') {
    const rows = pageRows.length > 0
      ? pageRows
      : rowsFromMainSlots(stripAliveAlbumShopMainSlots(bindings), slotContentTags, slotAlbumIds);
    return syncAliveAlbumPageMenu(rows, previous);
  }
  return syncCatalogMenuItemsWithBindings(
    bindings,
    previous,
    slotContentTags,
    slotAlbumIds,
  );
}

export function patchCatalogMenuDefaultId(
  items: readonly StorefrontMenuItem[],
  previousDefaultId: string | null,
): string | null {
  // Explicit "no default" — land on Hero / top, no menu highlight.
  if (previousDefaultId == null || previousDefaultId === '') return null;
  if (items.some((item) => item.id === previousDefaultId)) {
    return previousDefaultId;
  }
  // Default item was removed — do not invent another home.
  return null;
}

/** Toggle a scene radio view inside bodyHost. */
export function setCatalogMenuSceneViewEnabled(
  bindings: StorefrontBindings,
  previous: readonly StorefrontMenuItem[],
  viewType: StorefrontSceneViewType,
  enabled: boolean,
  slotContentTags: StorefrontSlotContentTags = {},
): StorefrontMenuItem[] {
  if (!storefrontUsesBodyHost(bindings)) {
    return syncCatalogMenuItemsWithBindings(bindings, previous, slotContentTags);
  }
  if (viewType === 'viewAbout' && storefrontShowsAboutAnchor(bindings)) {
    return syncCatalogMenuItemsWithBindings(bindings, previous, slotContentTags);
  }
  if (viewType === 'viewBio' && storefrontShowsBioAnchor(bindings)) {
    return syncCatalogMenuItemsWithBindings(bindings, previous, slotContentTags);
  }
  if (viewType === 'viewCatalog' && storefrontShowsCatalogAnchor(bindings)) {
    return syncCatalogMenuItemsWithBindings(bindings, previous, slotContentTags);
  }

  let next = previous.filter((item) => item.type !== viewType);
  if (enabled) {
    next = [...next, singletonItem(previous, viewType, AUTO_MENU_ITEM_IDS[viewType])];
  }
  const synced = syncCatalogMenuItemsWithBindings(bindings, next, slotContentTags);
  if (!synced.some((item) => SCENE_VIEW_TYPES.includes(item.type as StorefrontSceneViewType))) {
    return syncCatalogMenuItemsWithBindings(bindings, [
      ...synced,
      singletonItem([], 'viewCatalog', AUTO_MENU_ITEM_IDS.viewCatalog),
    ], slotContentTags);
  }
  return synced;
}

/** Reorder menu items (constructor arrows). */
export function moveCatalogMenuItem(
  items: readonly StorefrontMenuItem[],
  index: number,
  delta: number,
): StorefrontMenuItem[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return [...items];
  const next = [...items];
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row);
  return next;
}

export function listAvailableSceneViewAdds(
  bindings: StorefrontBindings,
  existing: readonly StorefrontMenuItem[],
): StorefrontSceneViewType[] {
  if (!storefrontUsesBodyHost(bindings)) return [];
  const has = (type: StorefrontSceneViewType) => existing.some((item) => item.type === type);
  const out: StorefrontSceneViewType[] = [];
  if (!has('viewCatalog') && !storefrontShowsCatalogAnchor(bindings)) out.push('viewCatalog');
  if (!has('viewBio') && !storefrontShowsBioAnchor(bindings)) out.push('viewBio');
  if (!has('viewAbout') && !storefrontShowsAboutAnchor(bindings)) out.push('viewAbout');
  return out;
}

const MENU_SINGLETON_TYPES: readonly StorefrontMenuItemType[] = [
  'catalogPopup',
  'scrollAbout',
  'scrollBio',
  'scrollCatalog',
  'scrollContacts',
  'scrollMessage',
  'scrollPortfolio',
  'viewCatalog',
  'viewAbout',
  'viewBio',
  'viewRecommendations',
];

function legacyScrollBlockContentTag(type: string): string | null {
  if (type === 'scrollBlog' || type === 'scrollBlock1') return 'my:block1';
  if (type === 'scrollBlock2') return 'my:block2';
  if (type === 'scrollBlock3') return 'my:block3';
  if (type === 'scrollBlock4') return 'my:block4';
  if (type === 'scrollBlock5') return 'my:block5';
  return null;
}

function normalizeMenuItemType(raw: unknown): StorefrontMenuItemType | null {
  const id = String(raw || '').trim();
  if (legacyScrollBlockContentTag(id)) return 'scrollNoteTag';
  return (STOREFRONT_MENU_ITEM_TYPES as readonly string[]).includes(id)
    ? (id as StorefrontMenuItemType)
    : null;
}

function normalizeSlotContentTags(raw: unknown): StorefrontSlotContentTags {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: StorefrontSlotContentTags = {};
  for (const slot of STOREFRONT_SLOTS) {
    const tag = normalizeContentTagRef((raw as Record<string, unknown>)[slot]);
    if (tag) out[slot] = tag;
  }
  return out;
}

const ALBUM_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeSlotAlbumIds(raw: unknown): StorefrontSlotAlbumIds {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: StorefrontSlotAlbumIds = {};
  for (const slot of STOREFRONT_SLOTS) {
    const id = String((raw as Record<string, unknown>)[slot] || '').trim().toLowerCase();
    if (ALBUM_ID_RE.test(id)) out[slot] = id;
  }
  return out;
}

/** An album slot without a gallery id is empty. Ids on other modules are dropped. */
function settleAlbumSlots(
  bindings: StorefrontBindings,
  slotAlbumIds: StorefrontSlotAlbumIds,
): { bindings: StorefrontBindings; slotAlbumIds: StorefrontSlotAlbumIds } {
  const nextBindings = { ...bindings };
  const nextIds: StorefrontSlotAlbumIds = {};
  for (const slot of STOREFRONT_SLOTS) {
    const id = slotAlbumIds[slot] || '';
    if (nextBindings[slot] === 'album' && id) {
      nextIds[slot] = id;
      continue;
    }
    if (nextBindings[slot] === 'album') nextBindings[slot] = 'none';
  }
  return { bindings: nextBindings, slotAlbumIds: nextIds };
}

export { contentTagDomId };

export function normalizeCatalogMenuItems(raw: unknown): StorefrontMenuItem[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return defaultCatalogMenuItems();
  }
  const seenSingleton = new Set<string>();
  const seenScrollNoteTags = new Set<string>();
  const seenCategoryIds = new Set<string>();
  const items: StorefrontMenuItem[] = [];

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const record = entry as Record<string, unknown>;
    const rawType = String(record.type || '').trim();
    const type = normalizeMenuItemType(rawType);
    if (!type) continue;
    const id = String(record.id || '').trim() || newStorefrontMenuItemId();

    if (type === 'scrollNoteTag') {
      const legacyTag = legacyScrollBlockContentTag(rawType);
      const contentTag = normalizeContentTagRef(
        record.contentTag ?? record.content_tag ?? legacyTag,
      );
      if (!contentTag) continue;
      const key = `scrollNoteTag:${contentTag}`;
      if (seenScrollNoteTags.has(key)) continue;
      seenScrollNoteTags.add(key);
      items.push({ id, type: 'scrollNoteTag', contentTag });
      continue;
    }

    if (type === 'scrollAlbum') {
      const galleryId = String(record.galleryId ?? record.gallery_id ?? '').trim().toLowerCase();
      if (!ALBUM_ID_RE.test(galleryId)) continue;
      const key = `scrollAlbum:${galleryId}`;
      if (seenScrollNoteTags.has(key)) continue;
      seenScrollNoteTags.add(key);
      items.push({ id, type: 'scrollAlbum', galleryId });
      continue;
    }

    if ((MENU_SINGLETON_TYPES as readonly string[]).includes(type)) {
      if (seenSingleton.has(type)) continue;
      seenSingleton.add(type);
      items.push({ id, type } as StorefrontMenuItem);
      continue;
    }

    const categoryId = String(record.categoryId || record.category_id || '').trim();
    if (!categoryId || seenCategoryIds.has(categoryId)) continue;
    seenCategoryIds.add(categoryId);
    items.push({ id, type: 'category', categoryId });
  }

  return items.length > 0 ? items : defaultCatalogMenuItems();
}

export function normalizeStorefrontContentWidth(raw: unknown): StorefrontContentWidth {
  const key = String(raw || '').trim().toLowerCase();
  if (key === 'full') return 'full';
  return 'narrow';
}

/** Tailwind classes for storefront content column (header / main / footer). */
export function storefrontContentInnerClass(
  width: StorefrontContentWidth,
  options?: { edgeToEdgeMobile?: boolean },
): string {
  // Mobile borderless body (hero / main / footer); header keeps gutter for chrome.
  const pad = options?.edgeToEdgeMobile ? 'px-0 sm:px-4' : 'px-4';
  return width === 'full'
    ? `mx-auto w-full max-w-none ${pad}`
    : `mx-auto w-full max-w-5xl ${pad}`;
}

export function normalizeStorefrontModuleSettings(raw: unknown): StorefrontModuleSettings {
  const source =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const catalogMenuRaw =
    source.catalogMenu && typeof source.catalogMenu === 'object' && !Array.isArray(source.catalogMenu)
      ? (source.catalogMenu as Record<string, unknown>)
      : {};
  const items = normalizeCatalogMenuItems(catalogMenuRaw.items);
  const rawDefault = String(
    catalogMenuRaw.defaultItemId ?? catalogMenuRaw.default_item_id ?? '',
  ).trim();
  const defaultItemId =
    rawDefault && items.some((item) => item.id === rawDefault)
      ? rawDefault
      : null;
  const layoutRaw = String(
    catalogMenuRaw.menuLayout ?? catalogMenuRaw.menu_layout ?? '',
  ).trim().toLowerCase();
  const menuLayout: StorefrontMenuLayout = STOREFRONT_MENU_LAYOUTS.includes(
    layoutRaw as StorefrontMenuLayout,
  )
    ? (layoutRaw as StorefrontMenuLayout)
    : 'auto';
  const scrollHomeRaw = catalogMenuRaw.scrollHomeOnLoad ?? catalogMenuRaw.scroll_home_on_load;
  const scrollHomeOnLoad =
    scrollHomeRaw === true
    || scrollHomeRaw === 'true'
    || scrollHomeRaw === 1
    || scrollHomeRaw === '1';
  const appearance = normalizeStorefrontAppearance(
    source.appearance,
    source.contentWidth ?? source.content_width,
  );
  const catalogSite = normalizeStorefrontCatalogSite(
    source.catalogSite ?? source.catalog_site,
  );
  const siteChrome = normalizeStorefrontSiteChrome(
    source.siteChrome ?? source.site_chrome,
  );
  let siteHero = normalizeStorefrontSiteHero(
    source.siteHero ?? source.site_hero,
  );
  // One-time fold: Appearance brandHero was a duplicate Hero photo uploader.
  let appearanceOut = appearance;
  if (!siteHero.heroImageUrl && appearance.brandHeroImageUrl) {
    siteHero = { ...siteHero, heroImageUrl: appearance.brandHeroImageUrl };
    appearanceOut = { ...appearance, brandHeroImageUrl: null };
  }
  const siteMessage = normalizeStorefrontSiteMessage(
    source.siteMessage ?? source.site_message,
  );
  const siteDictionaries = normalizeStorefrontSiteDictionaries(
    source.siteDictionaries ?? source.site_dictionaries,
    catalogSite,
  );
  const slotContentTags = normalizeSlotContentTags(
    source.slotContentTags ?? source.slot_content_tags,
  );
  const slotAlbumIds = normalizeSlotAlbumIds(
    source.slotAlbumIds ?? source.slot_album_ids,
  );
  const pageRows = normalizeAliveAlbumPageRows(source.pageRows ?? source.page_rows);
  const albumGridsChosen = source.albumGridsChosen === true || source.album_grids_chosen === true;
  const contactChrome = normalizeContactChrome(
    source.contactChrome ?? source.contact_chrome,
  );
  const shopSlides = normalizeShopSlides(source.shopSlides ?? source.shop_slides);
  const shopStartBanner = normalizeShopStartBanner(source.shopStartBanner ?? source.shop_start_banner);
  return {
    catalogMenu: {
      items,
      defaultItemId,
      menuLayout,
      scrollHomeOnLoad,
    },
    contentWidth: appearanceOut.contentWidth,
    appearance: appearanceOut,
    catalogSite,
    siteChrome,
    siteHero,
    siteMessage,
    siteDictionaries,
    slotContentTags,
    slotAlbumIds,
    pageRows,
    albumGridsChosen,
    contactChrome,
    shopSlides,
    shopStartBanner,
  };
}

export function normalizeStorefrontSettings(raw: unknown): TenantStorefrontSettings {
  if (!raw || typeof raw !== 'object') {
    return {
      homeEnabled: false,
      listOnPlatform: false,
      allowSearchIndexing: false,
      templateId: DEFAULT_STOREFRONT_SETTINGS.templateId,
      bindings: { ...DEFAULT_STOREFRONT_BINDINGS },
      moduleSettings: normalizeStorefrontModuleSettings(null),
    };
  }
  const record = raw as Record<string, unknown>;
  const templateId = String(record.templateId || record.template_id || '').trim()
    || DEFAULT_STOREFRONT_SETTINGS.templateId;
  const bindingsSource =
    record.bindings && typeof record.bindings === 'object' && !Array.isArray(record.bindings)
      ? (record.bindings as Record<string, unknown>)
      : record;
  const rawHeroMod = normalizeStorefrontModule(bindingsSource.hero);
  let bindings = normalizeStorefrontBindings(record.bindings ?? record);
  let moduleSettings = normalizeStorefrontModuleSettings(
    record.moduleSettings
    ?? record.module_settings
    ?? record.storefront_module_settings,
  );
  const migrated = migrateLegacyBlockBindings(bindings, moduleSettings.slotContentTags);
  bindings = migrated.bindings;
  moduleSettings = {
    ...moduleSettings,
    slotContentTags: migrated.slotContentTags,
  };
  const settled = settleAlbumSlots(bindings, moduleSettings.slotAlbumIds);
  bindings = settled.bindings;
  moduleSettings = {
    ...moduleSettings,
    slotAlbumIds: settled.slotAlbumIds,
  };
  if (brand.id === 'alivealbum') {
    const seeded = moduleSettings.pageRows.length > 0
      ? moduleSettings.pageRows
      : rowsFromMainSlots(bindings, moduleSettings.slotContentTags, moduleSettings.slotAlbumIds);
    const mirrored = mirrorAliveAlbumPageRows(
      bindings,
      moduleSettings.slotContentTags,
      moduleSettings.slotAlbumIds,
      seeded,
    );
    bindings = mirrored.bindings;
    moduleSettings = {
      ...moduleSettings,
      pageRows: seeded,
      slotContentTags: mirrored.slotContentTags,
      slotAlbumIds: mirrored.slotAlbumIds,
    };
  }
  // Preserve legacy slot intent while DB still has old `hero` values.
  if (rawHeroMod === 'none') {
    moduleSettings = {
      ...moduleSettings,
      siteHero: {
        ...moduleSettings.siteHero,
        showLogo: false,
        showTitle: false,
        heroImageUrl: null,
        heroImageUrlMobile: null,
        splashEnabled: false,
      },
    };
  } else if (rawHeroMod === 'brandLogo') {
    moduleSettings = {
      ...moduleSettings,
      siteHero: {
        ...moduleSettings.siteHero,
        showLogo: true,
        showTitle: false,
      },
    };
  }
  const items = syncBoundStorefrontMenu(
    bindings,
    moduleSettings.catalogMenu.items,
    moduleSettings.slotContentTags,
    moduleSettings.slotAlbumIds,
    moduleSettings.pageRows,
  );
  return {
    homeEnabled:
      record.homeEnabled === true
      || record.home_enabled === true
      || record.storefront_home_enabled === true,
    listOnPlatform:
      record.listOnPlatform === true
      || record.list_on_platform === true,
    allowSearchIndexing:
      record.allowSearchIndexing === true
      || record.allow_search_indexing === true,
    templateId,
    bindings,
    moduleSettings: {
      catalogMenu: {
        items,
        defaultItemId: patchCatalogMenuDefaultId(
          items,
          moduleSettings.catalogMenu.defaultItemId,
        ),
        menuLayout: moduleSettings.catalogMenu.menuLayout,
        scrollHomeOnLoad: moduleSettings.catalogMenu.scrollHomeOnLoad,
      },
      contentWidth: moduleSettings.appearance.contentWidth,
      appearance: moduleSettings.appearance,
      catalogSite: moduleSettings.catalogSite,
      siteChrome: moduleSettings.siteChrome,
      siteHero: moduleSettings.siteHero,
      siteMessage: moduleSettings.siteMessage,
      siteDictionaries: moduleSettings.siteDictionaries,
      slotContentTags: moduleSettings.slotContentTags,
      slotAlbumIds: moduleSettings.slotAlbumIds,
      pageRows: moduleSettings.pageRows,
      contactChrome: moduleSettings.contactChrome,
      shopSlides: moduleSettings.shopSlides,
      shopStartBanner: moduleSettings.shopStartBanner,
    },
  };
}

export function serializeStorefrontSettings(
  settings: TenantStorefrontSettings,
): TenantStorefrontSettings {
  return normalizeStorefrontSettings(settings);
}

/**
 * Whether a saved menu item can be shown on the live website right now.
 * `occupiedContentTags` — when set, tags without a note stay hidden (pass from public API).
 * Omit on settings so admins can wire menu before content exists.
 */
export function isStorefrontMenuItemRenderable(
  item: StorefrontMenuItem,
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
  knownCategoryIds?: ReadonlySet<string> | null,
  occupiedContentTags?: ReadonlySet<string>,
  siteDictionariesEnabled = true,
  knownAlbumIds?: ReadonlySet<string> | null,
  slotAlbumIds: StorefrontSlotAlbumIds = {},
  pageRows: readonly AliveAlbumStoredPageRow[] = [],
): boolean {
  const host = storefrontUsesBodyHost(bindings);
  const useRows = brand.id === 'alivealbum' && pageRows.length > 0;
  const rowHas = (moduleId: AliveAlbumPageModule, ref = '') => pageRows.some(
    (row) => row.moduleId === moduleId && (ref === '' || row.ref === ref),
  );
  if (item.type === 'catalogPopup') return false;
  if (item.type === 'viewCatalog' || item.type === 'viewAbout' || item.type === 'viewBio') {
    return host;
  }
  if (item.type === 'viewRecommendations') {
    return host && siteDictionariesEnabled;
  }
  if (item.type === 'scrollAbout') {
    return useRows ? rowHas('about') : storefrontShowsAboutAnchor(bindings);
  }
  if (item.type === 'scrollBio') {
    return useRows ? rowHas('bio') : storefrontShowsBioAnchor(bindings);
  }
  if (item.type === 'scrollCatalog') return storefrontShowsCatalogAnchor(bindings);
  if (item.type === 'scrollContacts') {
    return useRows ? rowHas('contacts') : storefrontShowsContactsAnchor(bindings);
  }
  if (item.type === 'scrollMessage') {
    return useRows ? rowHas('message') : storefrontShowsMessageAnchor(bindings);
  }
  if (item.type === 'scrollNoteTag') {
    const tag = normalizeContentTagRef(item.contentTag);
    if (!tag) return false;
    if (useRows) {
      if (!rowHas('noteTag', tag)) return false;
    } else if (!storefrontShowsNoteTagAnchor(bindings, slotContentTags, tag)) {
      return false;
    }
    if (occupiedContentTags && !occupiedContentTags.has(tag)) return false;
    return true;
  }
  if (item.type === 'scrollPortfolio') {
    return useRows
      ? rowHas('portfolio')
      : STOREFRONT_BODY_SLOTS.some((slot) => bindings[slot] === 'portfolio');
  }
  if (item.type === 'scrollAlbum') {
    if (!item.galleryId) return false;
    const bound = useRows
      ? rowHas('album', item.galleryId)
      : STOREFRONT_BODY_SLOTS.some(
        (slot) => bindings[slot] === 'album' && slotAlbumIds[slot] === item.galleryId,
      );
    if (!bound) return false;
    if (knownAlbumIds && !knownAlbumIds.has(item.galleryId)) return false;
    return true;
  }
  if (item.type === 'category') {
    if (!host) return false;
    if (!item.categoryId) return false;
    if (knownCategoryIds == null) return true;
    return knownCategoryIds.has(item.categoryId);
  }
  return false;
}

export type StorefrontMenuAddOption =
  | { type: 'catalogPopup' }
  | { type: 'scrollAbout' }
  | { type: 'scrollBio' }
  | { type: 'scrollCatalog' }
  | { type: 'scrollContacts' }
  | { type: 'scrollMessage' }
  | { type: 'scrollNoteTag'; contentTag: string }
  | { type: 'viewCatalog' }
  | { type: 'viewAbout' }
  | { type: 'viewBio' }
  | { type: 'viewRecommendations' }
  | { type: 'category'; categoryId: string; label: string };

/** Content tags wired to noteTag slots (deduped, stable order). */
export function listNoteTagSlots(
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
): string[] {
  const tags: string[] = [];
  const seen = new Set<string>();
  for (const slot of STOREFRONT_BODY_SLOTS) {
    if (bindings[slot] !== 'noteTag') continue;
    const tag = resolveSlotContentTag(slot, bindings, slotContentTags);
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    tags.push(tag);
  }
  return tags;
}

/** First body slot that shows this content tag (for menu / title resolution). */
export function findSlotForContentTag(
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
  contentTag: string,
): StorefrontSlotId | null {
  const tag = normalizeContentTagRef(contentTag);
  if (!tag) return null;
  for (const slot of STOREFRONT_BODY_SLOTS) {
    if (bindings[slot] !== 'noteTag') continue;
    if (resolveSlotContentTag(slot, bindings, slotContentTags) === tag) return slot;
  }
  return null;
}

/** Options the constructor may offer given current bindings + categories + existing items. */
export function listAvailableStorefrontMenuAddOptions(
  bindings: StorefrontBindings,
  slotContentTags: StorefrontSlotContentTags,
  existing: readonly StorefrontMenuItem[],
  categories: readonly { id: string; label: string }[],
  optionsExtra?: { recommendationsEnabled?: boolean },
): StorefrontMenuAddOption[] {
  const has = (type: StorefrontMenuItemType) => existing.some((i) => i.type === type);
  const hasScrollNoteTag = (tag: string) => existing.some(
    (i) => i.type === 'scrollNoteTag' && i.contentTag === tag,
  );
  const usedCategoryIds = new Set(
    existing.filter((i): i is Extract<StorefrontMenuItem, { type: 'category' }> => i.type === 'category')
      .map((i) => i.categoryId),
  );
  const options: StorefrontMenuAddOption[] = [];
  const host = storefrontUsesBodyHost(bindings);
  const recommendationsEnabled = optionsExtra?.recommendationsEnabled !== false;

  if (!has('catalogPopup')) {
    // Intentionally omitted — category navigation lives in WebsiteCatalogBrowse.
  }

  if (host) {
    if (!has('viewCatalog')) options.push({ type: 'viewCatalog' });
    if (!has('viewAbout') && !storefrontShowsAboutAnchor(bindings)) {
      options.push({ type: 'viewAbout' });
    }
    if (!has('viewBio') && !storefrontShowsBioAnchor(bindings)) {
      options.push({ type: 'viewBio' });
    }
    if (!has('viewRecommendations') && recommendationsEnabled) {
      options.push({ type: 'viewRecommendations' });
    }
    if (!has('scrollAbout') && storefrontShowsAboutAnchor(bindings)) {
      options.push({ type: 'scrollAbout' });
    }
    if (!has('scrollBio') && storefrontShowsBioAnchor(bindings)) {
      options.push({ type: 'scrollBio' });
    }
    if (!has('scrollCatalog') && storefrontShowsCatalogAnchor(bindings)) {
      options.push({ type: 'scrollCatalog' });
    }
    if (!has('scrollContacts') && storefrontShowsContactsAnchor(bindings)) {
      options.push({ type: 'scrollContacts' });
    }
    if (!has('scrollMessage') && storefrontShowsMessageAnchor(bindings)) {
      options.push({ type: 'scrollMessage' });
    }
    for (const tag of listNoteTagSlots(bindings, slotContentTags)) {
      if (!hasScrollNoteTag(tag)) {
        options.push({ type: 'scrollNoteTag', contentTag: tag });
      }
    }
  } else {
    if (!has('scrollAbout') && storefrontShowsAboutAnchor(bindings)) {
      options.push({ type: 'scrollAbout' });
    }
    if (!has('scrollBio') && storefrontShowsBioAnchor(bindings)) {
      options.push({ type: 'scrollBio' });
    }
    if (!has('scrollCatalog') && storefrontShowsCatalogAnchor(bindings)) {
      options.push({ type: 'scrollCatalog' });
    }
    if (!has('scrollContacts') && storefrontShowsContactsAnchor(bindings)) {
      options.push({ type: 'scrollContacts' });
    }
    if (!has('scrollMessage') && storefrontShowsMessageAnchor(bindings)) {
      options.push({ type: 'scrollMessage' });
    }
    for (const tag of listNoteTagSlots(bindings, slotContentTags)) {
      if (!hasScrollNoteTag(tag)) {
        options.push({ type: 'scrollNoteTag', contentTag: tag });
      }
    }
  }

  for (const cat of categories) {
    if (usedCategoryIds.has(cat.id)) continue;
    options.push({ type: 'category', categoryId: cat.id, label: cat.label });
  }
  return options;
}

/** First scene screen from menu; `none` when menu is only scroll anchors. */
export function defaultStorefrontBodyView(
  items: readonly StorefrontMenuItem[],
): StorefrontBodyViewId {
  for (const item of items) {
    if (item.type === 'viewCatalog' || item.type === 'category' || item.type === 'catalogPopup') {
      return 'catalog';
    }
    if (item.type === 'viewAbout') return 'about';
    if (item.type === 'viewBio') return 'bio';
    if (item.type === 'viewRecommendations') return 'recommendations';
  }
  return 'none';
}

export function storefrontMenuItemBodyView(
  item: StorefrontMenuItem,
): StorefrontBodyViewId | null {
  if (item.type === 'viewCatalog' || item.type === 'category' || item.type === 'catalogPopup') {
    return 'catalog';
  }
  if (item.type === 'viewAbout') return 'about';
  if (item.type === 'viewBio') return 'bio';
  if (item.type === 'viewRecommendations') return 'recommendations';
  return null;
}

/** Resolve home screen from constructor radio (defaultItemId). */
export function resolveCatalogMenuHome(
  items: readonly StorefrontMenuItem[],
  defaultItemId: string | null,
): {
  viewId: StorefrontBodyViewId;
  categoryId: string;
  itemId: string | null;
  /** Scroll to this element id after load (hero/footer anchors). */
  scrollTargetId: string | null;
} {
  // No default → stay on Hero / top; empty bodyHost scene; no menu highlight.
  if (!defaultItemId) {
    return { viewId: 'none', categoryId: '', itemId: null, scrollTargetId: null };
  }
  const chosen = items.find((item) => item.id === defaultItemId) ?? null;
  if (!chosen) {
    return { viewId: 'none', categoryId: '', itemId: null, scrollTargetId: null };
  }

  // Scene screens — switch bodyHost view.
  if (chosen.type === 'viewAbout') {
    return { viewId: 'about', categoryId: '', itemId: chosen.id, scrollTargetId: null };
  }
  if (chosen.type === 'viewBio') {
    return { viewId: 'bio', categoryId: '', itemId: chosen.id, scrollTargetId: null };
  }
  if (chosen.type === 'viewRecommendations') {
    return {
      viewId: 'recommendations',
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: null,
    };
  }
  if (chosen.type === 'category') {
    return {
      viewId: 'catalog',
      categoryId: chosen.categoryId,
      itemId: chosen.id,
      scrollTargetId: null,
    };
  }
  if (chosen.type === 'viewCatalog' || chosen.type === 'catalogPopup') {
    return { viewId: 'catalog', categoryId: '', itemId: chosen.id, scrollTargetId: null };
  }

  // Slot anchors — scroll only; scene stays empty unless a view* screen exists in the menu.
  if (chosen.type === 'scrollAbout') {
    return {
      viewId: defaultStorefrontBodyView(items),
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: 'website-module-about',
    };
  }
  if (chosen.type === 'scrollBio') {
    return {
      viewId: defaultStorefrontBodyView(items),
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: 'website-module-bio',
    };
  }
  if (chosen.type === 'scrollCatalog') {
    return {
      viewId: defaultStorefrontBodyView(items),
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: 'website-module-catalog',
    };
  }
  if (chosen.type === 'scrollContacts') {
    return {
      viewId: defaultStorefrontBodyView(items),
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: 'website-slot-footer',
    };
  }
  if (chosen.type === 'scrollMessage') {
    return {
      viewId: defaultStorefrontBodyView(items),
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: 'website-module-message',
    };
  }
  if (chosen.type === 'scrollNoteTag') {
    return {
      viewId: defaultStorefrontBodyView(items),
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: contentTagDomId(chosen.contentTag),
    };
  }
  if (chosen.type === 'scrollAlbum') {
    return {
      viewId: defaultStorefrontBodyView(items),
      categoryId: '',
      itemId: chosen.id,
      scrollTargetId: `website-module-album-${chosen.galleryId}`,
    };
  }

  // All StorefrontMenuItem types handled above.
  const _exhaustive: never = chosen;
  void _exhaustive;
  return { viewId: 'none', categoryId: '', itemId: null, scrollTargetId: null };
}

/**
 * Module painted inside bodyHost for the active scene view.
 * Null = empty scene (no view* selected, or that module already exists as a page anchor).
 */
export function resolveBodyHostModuleId(
  viewId: StorefrontBodyViewId,
  bindings: StorefrontBindings,
): StorefrontModuleId | null {
  if (viewId === 'none') return null;
  if (viewId === 'about') {
    if (storefrontShowsAboutAnchor(bindings)) return null;
    return 'about';
  }
  if (viewId === 'bio') {
    if (storefrontShowsBioAnchor(bindings)) return null;
    return 'bio';
  }
  if (viewId === 'catalog') {
    // Catalog already in page scroll — do not duplicate inside the empty scene.
    if (storefrontShowsCatalogAnchor(bindings)) return null;
    return 'catalog';
  }
  return null;
}

export function storefrontSlotLabelKey(slot: StorefrontSlotId): string {
  return `settings.storefrontSlot_${slot}`;
}

export function storefrontModuleLabelKey(moduleId: StorefrontModuleId): string {
  return `settings.storefrontModule_${moduleId}`;
}

export function storefrontMenuItemTypeLabelKey(type: StorefrontMenuItemType): string {
  return `settings.storefrontMenuItem_${type}`;
}
